// Whiteboard — the tool rail (what the pointer does) and the style bar (how
// the next thing looks — or, with something selected, how IT looks, plus
// what can be done with it). Sizes are the board's own, like paper.

import React, { useRef, useState } from 'react';
import { Icon, IconButton, Popover, Swatch, Tip } from '@modules/database/whiteboard/frontend/ui';
import { PALETTE, NOTE_COLORS, HAS_TEXT, SHAPES, isDrawing } from '@modules/database/whiteboard/frontend/engine/scene';
import { SYMBOL_KINDS } from '@modules/database/whiteboard/frontend/engine/symbols';
import { ZebraMark, HardHatZebra } from '@modules/database/whiteboard/frontend/ZebraArt';

export const SHAPE_TOOLS = [
  { id: 'rect', icon: 'rect', label: 'Rectangle', keys: 'R' },
  { id: 'ellipse', icon: 'ellipse', label: 'Ellipse', keys: 'O' },
  { id: 'diamond', icon: 'diamond', label: 'Diamond', keys: 'D' },
  { id: 'triangle', icon: 'triangle', label: 'Triangle' },
];
export const KEYMAP = { v: 'select', s: 'lasso', h: 'hand', p: 'pen', m: 'highlighter', e: 'eraser', l: 'line', a: 'arrow', t: 'text', n: 'note', i: 'image', k: 'laser' };
export const SHAPE_KEYS = { r: 'rect', o: 'ellipse', d: 'diamond' };

export const WIDTHS = {
  pen: [2, 4, 8, 14], highlighter: [12, 20, 32], shape: [1.5, 3, 5, 8],
};
const WIDTH_NAMES = ['Thin', 'Medium', 'Bold', 'Extra bold'];
export const FONT_SIZES = [{ v: 16, l: 'S' }, { v: 24, l: 'M' }, { v: 36, l: 'L' }, { v: 56, l: 'XL' }];
const HIGHLIGHTS = ['#facc15', '#4ade80', '#f472b6', '#60a5fa', '#fb923c'];
const FILLS = ['transparent', '#f1f5f9', '#fee2e2', '#ffedd5', '#fef9c3', '#dcfce7', '#cffafe', '#dbeafe', '#ede9fe', '#fce7f3'];
const COLOUR_NAMES = {
  '#1e293b': 'Ink', '#64748b': 'Grey', '#dc2626': 'Red', '#ea580c': 'Orange', '#ca8a04': 'Amber', '#16a34a': 'Green',
  '#0891b2': 'Teal', '#2563eb': 'Blue', '#7c3aed': 'Violet', '#db2777': 'Pink', transparent: 'No fill',
  '#facc15': 'Yellow', '#4ade80': 'Green', '#f472b6': 'Pink', '#60a5fa': 'Blue', '#fb923c': 'Orange',
  '#fef08a': 'Yellow', '#fed7aa': 'Orange', '#fecaca': 'Red', '#bbf7d0': 'Green', '#bae6fd': 'Blue', '#ddd6fe': 'Violet', '#e2e8f0': 'Grey',
};
const nameOf = (c) => COLOUR_NAMES[c] || c;

// ---- the tool rail ---------------------------------------------------------------------
// cols: 1 – 3 — a short board folds the rail into columns so every tool stays in view
export function ToolRail({ tool, setTool, shape, setShape, symbol, setSymbol, canEdit, onImage, onZebra, zebraRef, workItemKey, toolLock, setToolLock, side = 'right', cols = 1, railRef }) {
  const shapeBtn = useRef(null);
  const symBtn = useRef(null);
  const [shapesOpen, setShapesOpen] = useState(false);
  const [symsOpen, setSymsOpen] = useState(false);
  const symTool = SYMBOL_KINDS.find((k) => k.id === symbol) || SYMBOL_KINDS[1];
  const shapeTool = SHAPE_TOOLS.find((s) => s.id === shape) || SHAPE_TOOLS[0];
  // a shape-like tool goes back to Select after one; double-click keeps it
  const KEEPS = new Set(['shape', 'line', 'arrow', 'text', 'note', 'symbol']);
  const pick = (id) => { if (toolLock) setToolLock(false); setTool(id); };
  const keep = (id) => { if (!KEEPS.has(id)) return; setTool(id); setToolLock(true); };
  const kept = (id) => toolLock && tool === id && KEEPS.has(id);
  const B = (id, icon, label, keys) => (
    <IconButton key={id} icon={icon} keys={keys} side={side} active={tool === id} className={kept(id) ? 'is-kept' : ''}
      label={KEEPS.has(id) ? `${label}${kept(id) ? ' — kept (click to stop)' : ' — double-click to keep it'}` : label}
      onClick={() => pick(id)} onDoubleClick={() => keep(id)} />
  );
  return (
    <div ref={railRef} className={`wb-rail is-cols-${cols}`} role="toolbar" aria-label="Whiteboard tools">
      <Tip className="wb-brand-tip" label="Zebra Whiteboard" side={side}><span className="wb-rail-brand"><ZebraMark size={30} /></span></Tip>
      {B('select', 'select', 'Select', 'V')}
      {B('lasso', 'lasso', 'Lasso select', 'S')}
      {B('hand', 'hand', 'Move the view', 'H  Space')}
      {canEdit && <>
        <span className="wb-rail-sep" />
        {B('pen', 'pen', 'Pen', 'P')}
        {B('highlighter', 'highlighter', 'Highlighter', 'M')}
        {B('eraser', 'eraser', 'Eraser', 'E')}
        <span className="wb-rail-sep" />
        <IconButton icon={shapeTool.icon} label={`${shapeTool.label} — click again for more shapes${kept('shape') ? ' (kept)' : ''}`} keys={shapeTool.keys} side={side}
          active={tool === 'shape'} buttonRef={shapeBtn} className={`has-more ${kept('shape') ? 'is-kept' : ''}`}
          onClick={() => { if (tool === 'shape') setShapesOpen((o) => !o); else pick('shape'); }}
          onDoubleClick={() => { setShapesOpen(false); keep('shape'); }}
          onContextMenu={(e) => { e.preventDefault(); setShapesOpen(true); }}>
          <span className="wb-more-dot" />
        </IconButton>
        <Popover anchor={shapeBtn} open={shapesOpen} onClose={() => setShapesOpen(false)} side={side === 'right' ? 'right' : 'top'} className="wb-pop-row">
          {SHAPE_TOOLS.map((s) => (
            <IconButton key={s.id} icon={s.icon} label={s.label} keys={s.keys} active={shape === s.id && tool === 'shape'}
              onClick={() => { setShape(s.id); setTool('shape'); setShapesOpen(false); }} />
          ))}
        </Popover>
        {B('line', 'line', 'Line', 'L')}
        {B('arrow', 'arrow', 'Arrow', 'A')}
        <span className="wb-rail-sep" />
        {B('text', 'text', 'Text', 'T')}
        {B('note', 'note', 'Sticky note', 'N')}
        <IconButton icon="image" label="Picture or PDF from this computer" keys="I" side={side} onClick={onImage} />
        <span className="wb-rail-sep" />
        <IconButton icon="balloon" label={`Drawing Symbols${tool === 'symbol' ? ` — ${symTool.label}` : ''}${kept('symbol') ? ' (kept)' : ''}`} side={side}
          active={tool === 'symbol'} buttonRef={symBtn} className={`has-more ${kept('symbol') ? 'is-kept' : ''}`}
          onClick={() => { if (tool === 'symbol') setSymsOpen((o) => !o); else { pick('symbol'); setSymsOpen(true); } }}
          onDoubleClick={() => { setSymsOpen(false); keep('symbol'); }}>
          <span className="wb-more-dot" />
        </IconButton>
        <Popover anchor={symBtn} open={symsOpen} onClose={() => setSymsOpen(false)} side={side === 'right' ? 'right' : 'top'} className="wb-menu wb-sym-menu">
          <div className="wb-menu-title">Drawing Symbols</div>
          {SYMBOL_KINDS.map((k) => (
            <button key={k.id} type="button" className={symbol === k.id ? 'is-active' : ''} onClick={() => { setSymbol(k.id); setTool('symbol'); setSymsOpen(false); }}>
              <Icon name={k.id} size={18} />{k.label}
            </button>
          ))}
          <div className="wb-menu-hint">Click where it points (a dimension: drag from end to end), then fill it in.</div>
        </Popover>
      </>}
      <span className="wb-rail-sep" />
      {B('laser', 'laser', 'Laser pointer — everyone sees it', 'K')}
      {canEdit && <>
        <span className="wb-rail-sep" />
        <Tip className="wb-zebra-tip" label={`Bring in a drawing from ${workItemKey || 'this work item'} — its PDFs and pictures`} side={side}>
          <button ref={zebraRef} type="button" className="wb-btn wb-zebra-btn" aria-label="Bring in a drawing from this work item" onClick={onZebra}>
            <HardHatZebra size={32} />
          </button>
        </Tip>
      </>}
    </div>
  );
}

// ---- the style bar -----------------------------------------------------------------------
function Group({ label, children }) {
  return <div className="wb-sb-group" role="group" aria-label={label}><span className="wb-sb-label">{label}</span>{children}</div>;
}

function ColourRow({ colours, value, onPick, custom = true, label }) {
  return (
    <div className="wb-swatches">
      {colours.map((c) => <Swatch key={c} color={c} label={`${label}: ${nameOf(c)}`} active={value === c} onClick={() => onPick(c)} />)}
      {custom && (
        <Tip label={`${label}: any colour`}>
          <label className={`wb-swatch is-custom ${value && !colours.includes(value) ? 'is-active' : ''}`} style={value && !colours.includes(value) && value !== 'transparent' ? { '--sw': value } : undefined}>
            <input type="color" value={value && value.length === 7 ? value : '#1e293b'} onChange={(e) => onPick(e.target.value)} aria-label={`${label}: any colour`} />
          </label>
        </Tip>
      )}
    </div>
  );
}

// kind: what the bar is about — from the tool, or the selection's types.
export const SYMBOL_SIZES = [{ v: 12, l: 'S' }, { v: 16, l: 'M' }, { v: 22, l: 'L' }, { v: 32, l: 'XL' }];
export function StyleBar({ kinds, values, onChange, selection, actions, canEdit }) {
  if (!canEdit) return null;
  const onlySymbols = kinds.size > 0 && [...kinds].every((k) => k.startsWith('symbol'));
  if (onlySymbols) {
    const fs = values.symbolSize;
    const near = fs != null ? SYMBOL_SIZES.reduce((b, f) => (Math.abs(f.v - fs) < Math.abs(b.v - fs) ? f : b), SYMBOL_SIZES[0]).v : null;
    return (
      <div className="wb-stylebar" role="toolbar" aria-label="Style">
        <Group label="Colour"><ColourRow colours={PALETTE} value={values.stroke} label="Colour" onPick={(c) => onChange({ stroke: c })} /></Group>
        <Group label="Size">
          <div className="wb-seg">
            {SYMBOL_SIZES.map((f) => (
              <Tip key={f.v} label={`Annotation size ${f.l}`}>
                <button type="button" className={`wb-seg-btn wb-seg-text ${near === f.v ? 'is-active' : ''}`} aria-label={`Annotation size ${f.l}`} onClick={() => onChange({ symbolSize: f.v })}>{f.l}</button>
              </Tip>
            ))}
          </div>
        </Group>
        {selection && selection.length > 0 && <SelectionActions selection={selection} actions={actions} />}
      </div>
    );
  }
  const has = (...k) => k.some((x) => kinds.has(x));
  const isShape = [...kinds].some((k) => SHAPES.has(k));
  const sizeKind = has('pen') ? 'pen' : has('highlighter') ? 'highlighter' : (isShape || has('line', 'arrow')) ? 'shape' : null;
  const widths = sizeKind ? WIDTHS[sizeKind] : null;
  const width = values.width;
  const nearestWidth = widths && width != null ? widths.reduce((b, w) => (Math.abs(w - width) < Math.abs(b - width) ? w : b), widths[0]) : null;
  const font = values.fontSize;
  const nearestFont = font != null ? FONT_SIZES.reduce((b, f) => (Math.abs(f.v - font) < Math.abs(b.v - font) ? f : b), FONT_SIZES[0]).v : null;
  const showInk = has('pen', 'line', 'arrow') || isShape;
  const showText = has('text', 'note') || (selection && selection.some((e) => HAS_TEXT.has(e.type) && e.text));
  const textOnly = kinds.size === 1 && has('text');
  const groups = [];
  if (showInk) groups.push(<Group key="ink" label="Colour"><ColourRow colours={PALETTE} value={values.stroke} label="Colour" onPick={(c) => onChange({ stroke: c })} /></Group>);
  if (has('highlighter')) groups.push(<Group key="hl" label="Colour"><ColourRow colours={HIGHLIGHTS} value={values.highlight} label="Highlighter" onPick={(c) => onChange({ highlight: c })} /></Group>);
  if (textOnly) groups.push(<Group key="tc" label="Colour"><ColourRow colours={PALETTE} value={values.textColor} label="Text colour" onPick={(c) => onChange({ textColor: c })} /></Group>);
  if (isShape) groups.push(<Group key="fill" label="Fill"><ColourRow colours={FILLS} value={values.fill} label="Fill" onPick={(c) => onChange({ fill: c })} /></Group>);
  if (has('note')) groups.push(<Group key="note" label="Note"><ColourRow colours={NOTE_COLORS} value={values.noteColor} label="Note colour" custom={false} onPick={(c) => onChange({ noteColor: c })} /></Group>);
  if (widths) {
    groups.push(
      <Group key="w" label="Size">
        <div className="wb-seg">
          {widths.map((w, i) => (
            <Tip key={w} label={WIDTH_NAMES[i]}>
              <button type="button" className={`wb-seg-btn ${nearestWidth === w ? 'is-active' : ''}`} aria-label={WIDTH_NAMES[i]} onClick={() => onChange({ width: w, sizeKind })}>
                <span className="wb-dotsize" style={{ width: Math.min(18, 3 + i * 4), height: Math.min(18, 3 + i * 4) }} />
              </button>
            </Tip>
          ))}
        </div>
      </Group>,
    );
  }
  if (isShape || has('line', 'arrow')) {
    groups.push(
      <Group key="dash" label="Line">
        <div className="wb-seg">
          {[['solid', 'dashSolid', 'Solid'], ['dashed', 'dashDashed', 'Dashed'], ['dotted', 'dashDotted', 'Dotted']].map(([v, icon, l]) => (
            <IconButton key={v} icon={icon} label={l} className="wb-seg-btn" active={values.dash === v} onClick={() => onChange({ dash: v })} />
          ))}
        </div>
      </Group>,
    );
  }
  if (has('line', 'arrow')) {
    const heads = [['none', 'headNone', 'No end'], ['arrow', 'headArrow', 'Arrow'], ['triangle', 'headTriangle', 'Solid arrow'], ['dot', 'headDot', 'Dot'], ['bar', 'headBar', 'Bar']];
    groups.push(
      <Group key="heads" label="Ends">
        <HeadPicker label="Start" value={values.startHead} heads={heads} onPick={(v) => onChange({ startHead: v })} flip />
        <HeadPicker label="End" value={values.endHead} heads={heads} onPick={(v) => onChange({ endHead: v })} />
      </Group>,
    );
  }
  if (showText) {
    groups.push(
      <Group key="font" label="Text">
        <div className="wb-seg">
          {FONT_SIZES.map((f) => (
            <Tip key={f.v} label={`Text size ${f.l}`}>
              <button type="button" className={`wb-seg-btn wb-seg-text ${nearestFont === f.v ? 'is-active' : ''}`} aria-label={`Text size ${f.l}`} onClick={() => onChange({ fontSize: f.v })}>{f.l}</button>
            </Tip>
          ))}
        </div>
        <select className="wb-select" value={values.font || 'sans'} aria-label="Font" onChange={(e) => onChange({ font: e.target.value })}>
          <option value="sans">Sans</option><option value="serif">Serif</option><option value="mono">Mono</option>
        </select>
        <IconButton icon="bold" label="Bold" keys="Ctrl B" active={!!values.bold} onClick={() => onChange({ bold: !values.bold })} />
        {textOnly && (
          <div className="wb-seg">
            {[['left', 'alignLeft', 'Align left'], ['center', 'alignCenter', 'Centre'], ['right', 'alignRight', 'Align right']].map(([v, icon, l]) => (
              <IconButton key={v} icon={icon} label={l} className="wb-seg-btn" active={(values.align || 'left') === v} onClick={() => onChange({ align: v })} />
            ))}
          </div>
        )}
      </Group>,
    );
  }
  if (selection && selection.length === 1 && selection[0].type === 'image') {
    const d = selection[0].doc;
    groups.unshift(
      <div key="focus" className="wb-sb-group">
        <button type="button" className="wb-focus-btn" onClick={actions.focus}>
          <Icon name="focus" size={16} />{d && d.pages > 1 ? `Focus · page ${d.page} of ${d.pages}` : 'Focus'}
        </button>
        {isDrawing(selection[0]) && (
          <span className="wb-sb-hint">Drag it to move it — its markup moves with it</span>
        )}
      </div>,
    );
  }
  if (selection && selection.length) {
    groups.push(
      <Group key="op" label="Opacity">
        <input className="wb-range" type="range" min="10" max="100" step="5" aria-label="Opacity"
          value={Math.round((values.opacity ?? 1) * 100)} onChange={(e) => onChange({ opacity: Number(e.target.value) / 100 }, { live: true })}
          onPointerUp={(e) => onChange({ opacity: Number(e.currentTarget.value) / 100 })} onKeyUp={(e) => onChange({ opacity: Number(e.currentTarget.value) / 100 })} />
      </Group>,
    );
    groups.push(<SelectionActions key="acts" selection={selection} actions={actions} />);
  }
  if (!groups.length) return null;
  return <div className="wb-stylebar" role="toolbar" aria-label="Style">{groups}</div>;
}

function SelectionActions({ selection, actions }) {
  const allLocked = selection.every((e) => e.locked);
  const grouped = selection.some((e) => e.groupId);
  if (selection.some(isDrawing)) {
    // a drawing is the paper: no order, copy or lock — it moves with its markup, or leaves the board
    return (
      <div className="wb-sb-group wb-sb-actions">
        <IconButton icon="download" label="Export the marked-up drawing" onClick={actions.exportDrawing} />
        <IconButton icon="trash" label="Remove from the whiteboard (the file stays attached)" keys="Del" className="is-danger" onClick={() => actions.remove()} />
      </div>
    );
  }
  return (
    <div className="wb-sb-group wb-sb-actions">
      <IconButton icon="front" label="Bring to front" keys="Ctrl Shift ]" onClick={actions.front} />
      <IconButton icon="back" label="Send to back" keys="Ctrl Shift [" onClick={actions.back} />
      <IconButton icon="duplicate" label="Duplicate" keys="Ctrl D" onClick={actions.duplicate} />
      {selection.length > 1 && !grouped && <IconButton icon="group" label="Group" keys="Ctrl G" onClick={actions.group} />}
      {grouped && <IconButton icon="ungroup" label="Ungroup" keys="Ctrl Shift G" onClick={actions.ungroup} />}
      <IconButton icon={allLocked ? 'unlock' : 'lock'} label={allLocked ? 'Unlock' : 'Lock in place'} active={allLocked} onClick={actions.lock} />
      <IconButton icon="trash" label="Delete" keys="Del" className="is-danger" onClick={() => actions.remove()} />
    </div>
  );
}

function HeadPicker({ label, value, heads, onPick, flip }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const cur = heads.find((h) => h[0] === (value || 'none')) || heads[0];
  return (
    <>
      <Tip label={`${label}: ${cur[2]}`}>
        <button ref={ref} type="button" className="wb-btn wb-head-btn" aria-label={`${label}: ${cur[2]}`} onClick={() => setOpen((o) => !o)}>
          <Icon name={cur[1]} style={flip ? { transform: 'scaleX(-1)' } : undefined} />
          <Icon name="chevron" size={12} />
        </button>
      </Tip>
      <Popover anchor={ref} open={open} onClose={() => setOpen(false)} className="wb-pop-row">
        {heads.map(([v, icon, l]) => (
          <Tip key={v} label={l}>
            <button type="button" className={`wb-btn ${value === v ? 'is-active' : ''}`} aria-label={l} onClick={() => { onPick(v); setOpen(false); }}>
              <Icon name={icon} style={flip ? { transform: 'scaleX(-1)' } : undefined} />
            </button>
          </Tip>
        ))}
      </Popover>
    </>
  );
}
